import { useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * One-line "Add A Topic For Your Next 1:1" input.
 * Optimistic — the caller receives the created item via `onAdded(item)` and
 * can update its local state immediately. Also exposes a "Suggest A Topic"
 * popover that pulls 3 suggestions from the prompt-engine and lets the user
 * one-click-add any of them.
 */
export default function AgendaQuickAdd({
  pairingId,
  onAdded,
  compact = false,
  suggestions = true,
  autoFocus = false,
  placeholder = "Add A Topic For Your Next 1:1…",
  testIdPrefix = "quick-add",
}) {
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const submit = async (rawText) => {
    const clean = (rawText ?? title).trim();
    if (!clean) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rhythm/pairings/${pairingId}/agenda`, {
        title: clean,
        note: "",
      });
      setTitle("");
      onAdded?.(data);
      toast.success(`Added: ${clean.length > 40 ? clean.slice(0, 40) + "…" : clean}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't add that topic.");
    } finally {
      setSubmitting(false);
    }
  };

  const suggest = async () => {
    if (suggestOpen && suggested.length > 0) {
      setSuggestOpen(false);
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.get(`/rhythm/pairings/${pairingId}/suggest-prompts?count=3`);
      setSuggested(data);
      setSuggestOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't fetch suggestions.");
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="w-full" data-testid={`${testIdPrefix}-container`}>
      <div className="flex flex-wrap items-stretch gap-2">
        <input
          className="patria-input flex-1 min-w-[180px]"
          placeholder={placeholder}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          disabled={submitting}
          autoFocus={autoFocus}
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          className="patria-btn-primary shrink-0"
          onClick={() => submit()}
          disabled={submitting || !title.trim()}
          data-testid={`${testIdPrefix}-submit`}
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add
        </button>
        {suggestions && (
          <button
            type="button"
            className={compact ? "patria-btn-ghost shrink-0" : "patria-btn-secondary shrink-0"}
            onClick={suggest}
            disabled={suggesting}
            data-testid={`${testIdPrefix}-suggest`}
            title="Suggest A Topic"
          >
            <Sparkles className="h-4 w-4 mr-1" strokeWidth={1.5} /> Suggest A Topic
          </button>
        )}
      </div>
      {suggestOpen && suggested.length > 0 && (
        <div
          className="mt-2 rounded-2xl bg-cream-100 p-3 space-y-1"
          data-testid={`${testIdPrefix}-suggestion-tray`}
        >
          <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium mb-1">
            Suggestions From The Prompt Bank
          </p>
          {suggested.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { submit(s.text); setSuggestOpen(false); }}
              className="w-full text-left p-2 rounded-lg hover:bg-cream-200 transition text-sm text-forest-900 flex items-center justify-between gap-2"
              data-testid={`${testIdPrefix}-suggestion-${s.id}`}
            >
              <span>&ldquo;{s.text}&rdquo;</span>
              <Plus className="h-3.5 w-3.5 text-forest-700 shrink-0" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
